import React, { useMemo, useRef, useState } from 'react';

import downDisabledIcon from '@assets/img/icons/down-disabled.svg';
import downIcon from '@assets/img/icons/down.svg';
import { CheckboxTick } from '@components/Forms/CheckboxTick';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useField } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  multiDropdownContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.middleText,
    ...common.baseButton,
    height: 32,
    padding: '2px 6px',
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: 3,
  },
  multiDropdownDisplay: {
    alignSelf: 'stretch',
    minWidth: 0,
    flex: 1,
    paddingTop: 2,
  },
  multiDropdownInputOptionsContainer: {
    ...common.flexColumn,
    ...common.middleText,
    ...common.textGray700,
    marginTop: 4,
    backgroundColor: theme.palette.background.paper,
    padding: '8px 12px',
    borderRadius: 2,
    rowGap: 4,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
  },
  multiDropdownInputOption: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.baseButton,
    columnGap: 8,
  },
}));

export interface MultiDropdownOption<T> {
  label: string;
  value: T;
}

export interface MultiDropdownInputProps<T> extends React.ComponentProps<'input'> {
  containerStyle?: React.CSSProperties;
  options: MultiDropdownOption<T>[];
  name: string;
}

export const MultiDropdownInput = <T extends unknown>(props: MultiDropdownInputProps<T>) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { containerStyle, disabled, options, name } = props;

  const [field, , helpers] = useField<T[]>(name);

  const containerRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const renderOptions = () => {
    return options.map((option) => (
      <div
        key={option.value as any}
        className={classes.multiDropdownInputOption}
        onClick={() => {
          helpers.setValue(
            field.value.includes(option.value)
              ? field.value.filter((f) => f !== option.value)
              : [...field.value, option.value],
          );
        }}
      >
        <CheckboxTick checked={field.value.includes(option.value)} />

        {option.label}
      </div>
    ));
  };

  const labels = useMemo(
    () => field.value.map((v) => options.find((o) => o.value === v)?.label),
    [field.value, options],
  );

  const displayedLabel = useMemo(() => labels.join(', '), [labels]);

  return (
    <div
      ref={containerRef}
      style={containerStyle}
      className={classes.multiDropdownContainer}
      onClick={() => {
        setAnchorEl(anchorEl ? null : containerRef.current);
      }}
    >
      <div className={classes.multiDropdownDisplay} disabled {...props}>
        {displayedLabel}
      </div>

      <img
        alt="Toggle Menu"
        src={disabled ? downDisabledIcon : downIcon}
        style={{ width: 14, height: 12, marginRight: 4 }}
      />

      <Popper
        disablePortal={false}
        style={{ zIndex: 1400 }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        placement="bottom"
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <div
            className={classes.multiDropdownInputOptionsContainer}
            style={{ width: anchorEl?.clientWidth }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderOptions()}
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};
